import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';

/**
 * Contexto global de entidad activa (superconjunto del antiguo ActiveDocument).
 *
 * Publica la entidad que el usuario está viendo activamente para que VIAChatButton
 * pueda contextualizar el chat correctamente:
 *   - Documento (DocumentPanel, DocumentViewerPage, FichaPuestoEditor, FichaRolFuncionalEditor)
 *   - Persona (PersonaDetailPage)
 *   - Puesto (PuestoDetailPage)
 *   - Proceso (ProcessSheetPage)
 *
 * Alineado con el `VIANavContext` de mobile (packages/mobile/src/contexts/VIANavContext.tsx)
 * para mantener paridad arquitectural entre web y mobile.
 */

interface ActiveDocumentCtx {
  // Documento activo
  codigoDocumento: string | null;
  tituloDocumento: string | null;
  // Persona activa
  personaId: string | null;
  personaNombre: string | null;
  // Puesto activo
  puestoId: string | null;
  puestoTitulo: string | null;
  // Proceso activo
  procesoCodigo: string | null;
  procesoNombre: string | null;

  setActiveDocument: (codigo: string | null, titulo?: string | null) => void;
  setActivePersona: (id: string | null, nombre?: string | null) => void;
  setActivePuesto: (id: string | null, titulo?: string | null) => void;
  setActiveProceso: (codigo: string | null, nombre?: string | null) => void;
  /** Alias mantenido por retrocompatibilidad con 388b/388c — limpia solo el documento. */
  clearActiveDocument: () => void;
  /** Limpia todo el contexto (documento + persona + puesto + proceso). */
  clearAll: () => void;
}

const ActiveDocumentContext = createContext<ActiveDocumentCtx>({
  codigoDocumento: null,
  tituloDocumento: null,
  personaId: null,
  personaNombre: null,
  puestoId: null,
  puestoTitulo: null,
  procesoCodigo: null,
  procesoNombre: null,
  setActiveDocument: () => {},
  setActivePersona: () => {},
  setActivePuesto: () => {},
  setActiveProceso: () => {},
  clearActiveDocument: () => {},
  clearAll: () => {},
});

export function ActiveDocumentProvider({ children }: { children: ReactNode }) {
  const [codigoDocumento, setCodigoDoc] = useState<string | null>(null);
  const [tituloDocumento, setTituloDoc] = useState<string | null>(null);
  const [personaId, setPersonaIdState] = useState<string | null>(null);
  const [personaNombre, setPersonaNombreState] = useState<string | null>(null);
  const [puestoId, setPuestoIdState] = useState<string | null>(null);
  const [puestoTitulo, setPuestoTituloState] = useState<string | null>(null);
  const [procesoCodigo, setProcesoCodigoState] = useState<string | null>(null);
  const [procesoNombre, setProcesoNombreState] = useState<string | null>(null);

  const setActiveDocument = useCallback((codigo: string | null, titulo?: string | null) => {
    setCodigoDoc(codigo);
    setTituloDoc(titulo ?? null);
  }, []);

  const setActivePersona = useCallback((id: string | null, nombre?: string | null) => {
    setPersonaIdState(id);
    setPersonaNombreState(nombre ?? null);
  }, []);

  const setActivePuesto = useCallback((id: string | null, titulo?: string | null) => {
    setPuestoIdState(id);
    setPuestoTituloState(titulo ?? null);
  }, []);

  const setActiveProceso = useCallback((codigo: string | null, nombre?: string | null) => {
    setProcesoCodigoState(codigo);
    setProcesoNombreState(nombre ?? null);
  }, []);

  const clearActiveDocument = useCallback(() => {
    setCodigoDoc(null);
    setTituloDoc(null);
  }, []);

  const clearAll = useCallback(() => {
    setCodigoDoc(null);
    setTituloDoc(null);
    setPersonaIdState(null);
    setPersonaNombreState(null);
    setPuestoIdState(null);
    setPuestoTituloState(null);
    setProcesoCodigoState(null);
    setProcesoNombreState(null);
  }, []);

  return (
    <ActiveDocumentContext.Provider
      value={{
        codigoDocumento,
        tituloDocumento,
        personaId,
        personaNombre,
        puestoId,
        puestoTitulo,
        procesoCodigo,
        procesoNombre,
        setActiveDocument,
        setActivePersona,
        setActivePuesto,
        setActiveProceso,
        clearActiveDocument,
        clearAll,
      }}
    >
      {children}
    </ActiveDocumentContext.Provider>
  );
}

export function useActiveDocument() {
  return useContext(ActiveDocumentContext);
}
